import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import tw, { styled } from 'twin.macro';

import { strings } from '../../localization/strings';
import CommonButtonDefault from '../common/buttons/default';
import CouponsFilterProps from '../../entities/couponsFilter';
import { useAuth } from '../../hooks/use-auth';
import * as sitesService from '../../services/api/sites';
import * as productsService from '../../services/api/products';
import { Site } from '../../entities/site';
import { Product } from '../../entities/product';

interface ComponentProps {
  filterState: CouponsFilterProps;
  onUpdate: (filter: CouponsFilterProps) => void;
}

const StyledBlock = styled.div`
  ${tw`items-center flex`};
`;
const StyledLabel = styled.label`
  ${tw`inline-block text-sm text-gray-500 mb-1`};
`;

export default function CouponsFilter({ filterState, onUpdate }: ComponentProps) {
  const { token } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const initialState: CouponsFilterProps = {
    isActive: filterState.isActive || '',
    siteId: filterState.siteId || '',
    productId: filterState.productId || '',
  };

  const emptyState: CouponsFilterProps = {
    isActive: '',
    siteId: '',
    productId: '',
  };

  const [filter, setFilter] = useState<CouponsFilterProps>(initialState);

  const statusOptions = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Active only' },
    { value: 'false', label: 'Inactive only' },
  ];

  const siteOptions = [
    { value: '', label: 'All' },
    ...sites.map((site) => ({
      value: site.id.toString(),
      label: site.domain,
    })),
  ];

  const productOptions = [
    { value: '', label: 'All' },
    ...products.map((product) => ({
      value: product.id.toString(),
      label: product.title,
    })),
  ];

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const data = await sitesService.list(token);

        setSites(data || []);
      } catch (e) {
        // Failed to fetch sites
      }
    };

    fetchSites();
  }, [token]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!filter.siteId) {
        setProducts([]);

        return;
      }

      try {
        const data = await productsService.list(parseInt(filter.siteId, 10));

        setProducts(data || []);
      } catch (e) {
        // Failed to fetch products
      }
    };

    fetchProducts();
  }, [filter.siteId]);

  const onStatusChange = (event: { value: string } | null) => {
    if (!event) return;

    setFilter({ ...filter, isActive: event.value });
    onUpdate({ ...filter, isActive: event.value });
  };

  const onSiteChange = (event: { value: string } | null) => {
    if (!event) return;

    const newFilter = { ...filter, siteId: event.value, productId: '' };

    setFilter(newFilter);
    onUpdate(newFilter);
  };

  const onProductChange = (event: { value: string } | null) => {
    if (!event) return;

    setFilter({ ...filter, productId: event.value });
    onUpdate({ ...filter, productId: event.value });
  };

  const clearFilter = () => {
    setFilter(emptyState);
    onUpdate(emptyState);
  };

  return (
    <div className="mb-4 bg-white w-full shadow sm:rounded-lg">
      <div className="flex flex-wrap space-y-2 lg:space-y-0 px-4 py-4 space-x-4 items items-stretch">
        <StyledBlock>
          <div>
            <StyledLabel htmlFor="status">Status:</StyledLabel>
            <Select
              className="w-40 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              options={statusOptions}
              value={statusOptions.filter((option) => option.value === filter.isActive)}
              onChange={(event) => onStatusChange(event)}
            />
          </div>
        </StyledBlock>
        <StyledBlock>
          <div>
            <StyledLabel htmlFor="site">{strings.filter.site}:</StyledLabel>
            <Select
              className="w-56 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              options={siteOptions}
              value={siteOptions.filter((option) => option.value === filter.siteId)}
              onChange={(event) => onSiteChange(event)}
            />
          </div>
        </StyledBlock>
        {filter.siteId && (
          <StyledBlock>
            <div>
              <StyledLabel htmlFor="product">{strings.filter.product}:</StyledLabel>
              <Select
                className="w-56 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                options={productOptions}
                value={productOptions.filter((option) => option.value === filter.productId)}
                onChange={(event) => onProductChange(event)}
              />
            </div>
          </StyledBlock>
        )}
        <StyledBlock className="!items-end">
          <CommonButtonDefault onClick={clearFilter} className="w-full md:w-auto inline-flex">
            {strings.filter.clearFilter}
          </CommonButtonDefault>
        </StyledBlock>
      </div>
    </div>
  );
}
